<template>
  <div>
    <app-cells>
      <h1 class="title">Advances</h1>
      <app-button @click.native="onShowModal">Create Advance</app-button>
    </app-cells>
    <app-spinner v-if="!page_loaded"/>
    <template v-else>
      <div class="info-cards">
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Awaiting Payroll Deduction:</span> <span>{{ formatAmount(loans_summary.awaiting_payroll_deduction) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Disbursed Advances:</span> <span>{{ loans_summary.disbursed_count || 0 }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Total Disbursed:</span> <span>{{ formatAmount(loans_summary.total_disbursed_lifetime) }}</span>
            </div>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Pending Requests:</span> <span>{{ loans_summary.pending_requests || 0 }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Requested Amount:</span> <span>{{ formatAmount(loans_summary.requested_amount) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Rejected:</span> <span>{{ loans_summary.rejected_failed_check || 0 }}</span>
            </div>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Archived:</span> <span>{{ loans_summary.archived_repaid_count || 0 }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Total Repaid:</span> <span>{{ formatAmount(loans_summary.total_repaid_lifetime) }}</span>
            </div>
          </div>
        </div>
      </div>
      <app-tabs :tabs="tabs"/>
      <transition mode="out-in" name="fade">
        <router-view ref="advancesList" @confirm-action="onShowActionConfirmation"/>
      </transition>
    </template>

    <modal
      name="advance-form"
      :width="960"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      @closed="resetEligibilityPreview"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="closeFormModal" class="popup__close" type="button"></button>
        <div class="popup__title">Create Advance</div>
        <div class="popup__body advances__modal-body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Employee" required>
              <app-select
                v-model="form.employee"
                :options="employee_options"
                :clearable="false"
                :error="$v.form.employee.$error"
                label="full_name"
                placeholder="Employee"
                @search="onEmployeeSearch"
              />
              <template #error>
                <div v-if="$v.form.employee.$dirty && !$v.form.employee.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Amount" label-for="advance-amount" required>
              <app-fin-number
                v-model="form.amount"
                :error="$v.form.amount.$error"
                id="advance-amount"
                name="amount"
                placeholder="0"
              />
              <template #error>
                <div v-if="$v.form.amount.$dirty && !$v.form.amount.required">Required field</div>
                <div v-else-if="$v.form.amount.$dirty && !$v.form.amount.minValue">Minimum advance is 30,000 TZS</div>
              </template>
            </app-form-group>
            <app-form-group label="Employee notes" label-for="advance-employee-notes">
              <app-textarea
                v-model="form.employee_notes"
                id="advance-employee-notes"
                name="employee_notes"
                placeholder="Employee notes"
              />
            </app-form-group>
            <app-cells :indent="false" position="center">
              <app-button v-if="canSubmitAdvance" ref="advance_create" type="submit">
                Create
              </app-button>
            </app-cells>
          </form>
          <div class="advances__eligibility">
            <div v-if="eligibility_preview_loading && eligibility_preview" class="advances__eligibility-loader">
              <img alt="" src="@/assets/img/preloader.svg">
            </div>
            <template v-if="eligibility_preview">
              <div class="advances__eligibility-head">
                <div class="advances__eligibility-title">Eligibility status</div>
                <span
                  :class="{ 'advances__eligibility-status--error': !eligibility_preview.eligible }"
                  class="advances__eligibility-status"
                >
                  {{ eligibility_preview.eligible ? 'Eligible' : 'Not eligible' }}
                </span>
              </div>
              <div class="advances__eligibility-main">
                <div class="advances__eligibility-label">Salary after request</div>
                <div class="advances__eligibility-value advances__eligibility-value--large">
                  {{ formatAmount(eligibility_preview.accrued_salary_after) }}
                </div>
                <div class="advances__eligibility-hint">{{ eligibility_preview.accrued_salary_percent_after }}% after request</div>
              </div>
              <div class="advances__eligibility-grid">
                <div class="advances__eligibility-card">
                  <div class="advances__eligibility-label">Gross salary</div>
                  <div class="advances__eligibility-value">{{ formatAmount(eligibility_preview.gross_salary) }}</div>
                </div>
                <div class="advances__eligibility-card">
                  <div class="advances__eligibility-label">Net salary before loans/advance</div>
                  <div class="advances__eligibility-value">{{ formatAmount(eligibility_preview.net_salary_before_debt) }}</div>
                </div>
              </div>
              <div class="advances__eligibility-request">
                <div class="advances__eligibility-label">Requested advance</div>
                <div class="advances__eligibility-value advances__eligibility-value--large">
                  {{ formatAmount(eligibility_preview.new_monthly_payment) }} / {{ formatAmount(eligibility_preview.available_monthly_deduction) }}
                </div>
                <div class="advances__eligibility-hint">requested / available</div>
              </div>
              <div
                v-if="eligibility_preview.heslb_amount > 0 || eligibility_preview.existing_staff_loan_amount > 0 || eligibility_preview.existing_advance_amount > 0"
                class="advances__eligibility-deductions"
              >
                <div v-if="eligibility_preview.heslb_amount > 0" class="advances__eligibility-deduction">
                  Existing HESLB deduction: <b>{{ formatAmount(eligibility_preview.heslb_amount) }}</b>
                </div>
                <div v-if="eligibility_preview.existing_staff_loan_amount > 0" class="advances__eligibility-deduction">
                  Existing staff loan deductions: <b>{{ formatAmount(eligibility_preview.existing_staff_loan_amount) }}</b>
                </div>
                <div v-if="eligibility_preview.existing_advance_amount > 0" class="advances__eligibility-deduction">
                  Existing advance deductions: <b>{{ formatAmount(eligibility_preview.existing_advance_amount) }}</b>
                </div>
              </div>
              <div v-if="!eligibility_preview.eligible" class="advances__eligibility-reason">
                <b>Reason:</b> {{ eligibility_preview.reason && eligibility_preview.reason.name }}
              </div>
            </template>
            <div v-else-if="eligibility_preview_loading" class="advances__eligibility-placeholder">
              <img alt="" src="@/assets/img/preloader.svg">
            </div>
            <div v-else-if="eligibility_preview_error" class="advances__eligibility-placeholder">
              Unable to load eligibility preview
            </div>
            <div v-else class="advances__eligibility-placeholder">
              Select employee and amount to see eligibility preview
            </div>
          </div>
        </div>
      </div>
    </modal>

    <modal
      name="advance-action-confirmation"
      :width="400"
      :height="'auto'"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="cancelAction" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex"><img src="@/assets/img/warning-round-icon.svg" alt=""> Confirm action</div>
        <div class="popup__body">
          <div class="popup__subt">
            Are you sure you want to {{ pendingAction ? pendingAction.action.label.toLowerCase() : '' }} this advance?
          </div>
          <app-form-group v-if="pendingAction && pendingAction.action.code === 'reject'" label="Rejection reason">
            <app-textarea v-model="rejection_reason" name="rejection_reason" placeholder="Rejection reason"/>
          </app-form-group>
          <app-cells :indent="false" position="center">
            <app-button theme="dark" @click.native="cancelAction">Cancel</app-button>
            <app-button ref="action_preload" @click.native="confirmAction">Confirm</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getEmployeesAdvancesEligibilityPreview,
  getEmployeesAdvancesSummary,
  getEmployeesListSearch,
  postEmployeeAdvances,
  postEmployeesAdvancesDisburse,
  postEmployeesAdvancesReject,
} from '@/http'
import AppTabs from '@/components/AppTabs'
import { minValue, required } from 'vuelidate/lib/validators'
import debounce from 'lodash/debounce'

const actionRequests = {
  disburse: postEmployeesAdvancesDisburse,
  reject: postEmployeesAdvancesReject,
}

export default {
  name: 'AdvancesLayout',
  components: {AppTabs},
  data() {
    return {
      tabs: [
        {name: 'employees-advances', label: 'Requests'},
        {name: 'employees-advances-disbursed', label: 'Disbursed'},
        {name: 'employees-advances-rejected', label: 'Rejected'},
        {name: 'employees-advances-archive', label: 'Archive'},
      ],
      loans_summary: {},
      page_loaded: false,
      form: {
        employee: null,
        amount: null,
        employee_notes: '',
      },
      employee_options: [],
      eligibility_preview: null,
      eligibility_preview_loading: false,
      eligibility_preview_error: false,
      eligibilityPreviewRequestId: 0,
      eligibilityPreviewTimeout: null,
      pendingAction: null,
      actionLoadingId: null,
      rejection_reason: '',
    }
  },
  validations: {
    form: {
      employee: { required },
      amount: { required, minValue: minValue(30000) },
    }
  },
  computed: {
    eligibilityIsIneligible() {
      return this.eligibility_preview && this.eligibility_preview.eligible === false
    },
    canSubmitAdvance() {
      return Boolean(this.eligibility_preview?.eligible) && !this.$v.form.$invalid
    },
  },
  watch: {
    'form.employee': 'onEligibilityParamsChange',
    'form.amount': 'onEligibilityParamsChange',
  },
  created() {
    this.fetchSummary()
  },
  methods: {
    fetchSummary() {
      return getEmployeesAdvancesSummary()
        .then(response => {
          this.loans_summary = response.data
          this.page_loaded = true
        })
        .catch(() => {
          this.page_loaded = true
        })
    },
    onShowModal() {
      this.form = {
        employee: null,
        amount: null,
        employee_notes: '',
      }
      this.employee_options = []
      this.resetEligibilityPreview()
      this.$v.form.$reset()
      this.$modal.show('advance-form')
    },
    closeFormModal() {
      this.$modal.hide('advance-form')
    },
    onCheckForm() {
      if (this.eligibilityIsIneligible) {
        this.$notify({
          type: 'info',
          title: 'This advance is not eligible',
        })
        return
      }

      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
        return
      }

      this.sendForm()
    },
    sendForm() {
      this.$refs.advance_create.preload = true
      postEmployeeAdvances({
        employee: this.form.employee.id,
        amount: this.form.amount,
        employee_notes: this.form.employee_notes || '',
      })
        .then(() => {
          this.$modal.hide('advance-form')
          this.fetchSummary()
          if (this.$refs.advancesList && this.$refs.advancesList.fetchItems) {
            return this.$refs.advancesList.fetchItems()
          }
        })
        .then(() => {
          this.$notify({ type: 'success', title: 'Advance created' })
        })
        .catch(() => {
          this.$refs.advance_create.preload = false
        })
    },
    onEligibilityParamsChange() {
      if (!this.hasEligibilityParams()) {
        this.resetEligibilityPreview()
        return
      }

      this.scheduleEligibilityPreview()
    },
    hasEligibilityParams() {
      const employeeId = this.form.employee && this.form.employee.id
      const amount = Number(this.form.amount)

      return Boolean(employeeId) && Number.isFinite(amount) && amount > 0
    },
    scheduleEligibilityPreview() {
      clearTimeout(this.eligibilityPreviewTimeout)
      this.eligibilityPreviewTimeout = setTimeout(() => this.fetchEligibilityPreview(), 350)
    },
    fetchEligibilityPreview() {
      if (!this.hasEligibilityParams()) {
        this.resetEligibilityPreview()
        return
      }

      const requestId = this.eligibilityPreviewRequestId + 1
      this.eligibilityPreviewRequestId = requestId
      this.eligibility_preview_loading = true
      this.eligibility_preview_error = false

      getEmployeesAdvancesEligibilityPreview(this.form.employee.id, Number(this.form.amount))
        .then(response => {
          if (requestId !== this.eligibilityPreviewRequestId) return
          this.eligibility_preview = response.data
        })
        .catch(() => {
          if (requestId !== this.eligibilityPreviewRequestId) return
          this.eligibility_preview_error = true
        })
        .then(() => {
          if (requestId === this.eligibilityPreviewRequestId) this.eligibility_preview_loading = false
        })
    },
    resetEligibilityPreview() {
      clearTimeout(this.eligibilityPreviewTimeout)
      this.eligibilityPreviewTimeout = null
      this.eligibilityPreviewRequestId += 1
      this.eligibility_preview = null
      this.eligibility_preview_loading = false
      this.eligibility_preview_error = false
    },
    onEmployeeSearch(search, loading) {
      loading(true)
      this.searchEmployee(loading, search, this)
    },
    searchEmployee: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.employee_options = []
        return
      }

      getEmployeesListSearch({search})
        .then(response => {
          vm.employee_options = response.data
        })
        .finally(() => loading(false))
    }, 350),
    onShowActionConfirmation(payload) {
      if (this.actionLoadingId !== null) return

      this.pendingAction = payload
      this.rejection_reason = ''
      this.$modal.show('advance-action-confirmation')
    },
    cancelAction() {
      if (this.actionLoadingId !== null) return

      this.pendingAction = null
      this.rejection_reason = ''
      this.$modal.hide('advance-action-confirmation')
    },
    confirmAction() {
      if (!this.pendingAction || this.actionLoadingId !== null) return

      const { row, action } = this.pendingAction
      this.actionLoadingId = row.id
      this.$refs.action_preload.preload = true
      const data = action.code === 'reject' ? {rejection_reason: this.rejection_reason} : undefined

      actionRequests[action.code](row.id, data)
        .then(() => {
          this.$modal.hide('advance-action-confirmation')
          this.pendingAction = null
          this.fetchSummary()
          if (this.$refs.advancesList && this.$refs.advancesList.fetchItems) {
            return this.$refs.advancesList.fetchItems()
          }
        })
        .then(() => {
          this.actionLoadingId = null
        })
        .catch(() => {
          this.actionLoadingId = null
          this.$refs.action_preload.preload = false
        })
    },
    formatAmount(amount) {
      return `${Number(amount || 0).toLocaleString()} TSh`
    },
  }
}
</script>

<style lang="sass">
.advances__modal-body
  display: grid
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr)
  gap: 24px

.advances__eligibility
  position: relative
  min-height: 240px
  padding: 16px
  border: 1px solid #d9e0e8
  border-radius: 8px
  background: #f8fafc

.advances__eligibility-head
  display: flex
  align-items: center
  justify-content: space-between
  margin-bottom: 14px

.advances__eligibility-title
  color: #606770
  font-weight: 700
  text-transform: uppercase

.advances__eligibility-status
  padding: 7px 10px
  border-radius: 5px
  color: #398c62
  background: #dcf3df
  font-weight: 700

.advances__eligibility-status--error
  color: #c84a4a
  background: #fbe1e1

.advances__eligibility-main,
.advances__eligibility-card,
.advances__eligibility-request
  padding: 14px
  border: 1px solid #d9e0e8
  border-radius: 8px
  background: #fff

.advances__eligibility-main
  margin-bottom: 14px

.advances__eligibility-grid
  display: grid
  grid-template-columns: repeat(2, minmax(0, 1fr))
  gap: 12px
  margin-bottom: 14px

.advances__eligibility-request
  border-color: #dce9bd
  background: #fbfff3

.advances__eligibility-label,
.advances__eligibility-hint
  color: #747b84

.advances__eligibility-value
  margin-top: 4px
  font-size: 20px
  font-weight: 700

.advances__eligibility-value--large
  font-size: 25px

.advances__eligibility-deductions,
.advances__eligibility-reason,
.advances__eligibility-warning
  margin-top: 16px
  padding-top: 16px
  border-top: 1px solid #d9e0e8

.advances__eligibility-deduction + .advances__eligibility-deduction
  margin-top: 8px

.advances__eligibility-reason,
.advances__eligibility-warning
  color: #c84a4a

.advances__eligibility-placeholder
  display: flex
  align-items: center
  justify-content: center
  min-height: 208px
  color: #747b84
  text-align: center

  img
    width: 32px
    height: 32px

.advances__eligibility-loader
  position: absolute
  z-index: 1
  top: 0
  right: 0
  bottom: 0
  left: 0
  display: flex
  align-items: center
  justify-content: center
  border-radius: 8px
  background: rgba(248, 250, 252, 0.7)

  img
    width: 36px
    height: 36px

@media (max-width: 767px)
  .advances__modal-body
    grid-template-columns: 1fr
</style>
